import {
    ASSIGN_SECTION_ACCESS,
    REVIEWER_TYPE_USER,
    UNASSIGN_SECTION_ACCESS
} from '@/utils/constants/constants.js';
import axios from 'axios';

export const manage_assigned_workflows_section_access_mixin = {
    methods: {
        // TODO: Revisit section access assignments in detail.
        // The current page-specific flow reduces modal friction and adds bulk actions,
        // but we should review the broader assignment model and UX together later.
        build_reviewer_section_access_payload() {
            const payload = {};

            if (this.edit_reviewer_type === REVIEWER_TYPE_USER) {
                Reflect.set(payload, 'workflow_reviewer_user_id', this.edit_workflow_reviewer_id);
            } else {
                Reflect.set(payload, 'workflow_reviewer_role_id', this.edit_workflow_reviewer_id);
            }

            const result = payload;
            return result;
        },

        build_section_action_map(section_id, action_id) {
            const section_actions = {};
            const section_key = String(section_id);
            Reflect.set(section_actions, section_key, [action_id]);
            const result = section_actions;
            return result;
        },

        build_bulk_section_action_map(action_id) {
            const section_actions = {};

            this.sections_with_access.forEach(section => {
                const section_key = String(section.id);
                Reflect.set(section_actions, section_key, [action_id]);
            });

            const result = section_actions;
            return result;
        },

        async post_section_access_assignment(payload, success_message = '') {
            const response = await axios.post(ASSIGN_SECTION_ACCESS, payload, {
                headers: this.headers
            });
            const response_status = Reflect.get(Reflect.get(response, 'data'), 'status');

            if (response_status !== 'success') {
                throw new Error('Failed to assign section access');
            }

            if (success_message) {
                this.show_success(success_message);
            }
        },

        async post_section_access_unassignment(payload, success_message = '') {
            const response = await axios.post(UNASSIGN_SECTION_ACCESS, payload, {
                headers: this.headers
            });
            const response_status = Reflect.get(Reflect.get(response, 'data'), 'status');

            if (response_status !== 'success') {
                throw new Error('Failed to remove section access');
            }

            if (success_message) {
                this.show_success(success_message);
            }
        },

        async refresh_section_access_modal_state() {
            await this.load_existing_section_access();
            this.build_sections_with_access();
        },

        async toggle_section_read_access(section) {
            this.isLoading = true;

            try {
                await this.process_read_access_change(section);
                await this.refresh_section_access_modal_state();
            } catch (error) {
                this.show_error('Error updating read access');
            }

            this.isLoading = false;
        },

        async process_read_access_change(section) {
            if (section.has_read && section.read_access_id) {
                const payload = { section_access_id: section.read_access_id };
                await this.post_section_access_unassignment(payload);
                return;
            }

            if (section.has_write && section.write_access_id) {
                const remove_payload = { section_access_id: section.write_access_id };
                await this.post_section_access_unassignment(remove_payload);
            }

            const reviewer_payload = this.build_reviewer_section_access_payload();
            const section_actions = this.build_section_action_map(section.id, this.read_action_id);
            Reflect.set(reviewer_payload, 'section_actions', section_actions);
            await this.post_section_access_assignment(reviewer_payload);
        },

        async toggle_section_write_access(section) {
            this.isLoading = true;

            try {
                await this.process_write_access_change(section);
                await this.refresh_section_access_modal_state();
            } catch (error) {
                this.show_error('Error updating write access');
            }

            this.isLoading = false;
        },

        async process_write_access_change(section) {
            if (section.has_write && section.write_access_id) {
                const payload = { section_access_id: section.write_access_id };
                await this.post_section_access_unassignment(payload);
                return;
            }

            if (section.has_read && section.read_access_id) {
                const remove_payload = { section_access_id: section.read_access_id };
                await this.post_section_access_unassignment(remove_payload);
            }

            const reviewer_payload = this.build_reviewer_section_access_payload();
            const section_actions = this.build_section_action_map(section.id, this.write_action_id);
            Reflect.set(reviewer_payload, 'section_actions', section_actions);
            await this.post_section_access_assignment(reviewer_payload);
        },

        async assign_read_access_to_all() {
            await this.assign_bulk_section_access(this.read_action_id, 'Read access assigned to all sections');
        },

        async assign_write_access_to_all() {
            await this.assign_bulk_section_access(this.write_action_id, 'Write access assigned to all sections');
        },

        async assign_bulk_section_access(action_id, success_message) {
            if (!action_id || !this.sections_with_access.length) {
                return;
            }

            this.isLoading = true;

            try {
                await this.clear_all_section_access(false);
                const payload = this.build_reviewer_section_access_payload();
                const section_actions = this.build_bulk_section_action_map(action_id);
                Reflect.set(payload, 'section_actions', section_actions);
                await this.post_section_access_assignment(payload, success_message);
                await this.refresh_section_access_modal_state();
            } catch (error) {
                this.show_error('Error assigning access to all sections');
            }

            this.isLoading = false;
        },

        async clear_all_section_access(show_success_message = true) {
            if (!this.edit_workflow_reviewer_id) {
                return;
            }

            const payload = this.build_reviewer_section_access_payload();
            const success_message = show_success_message ? 'Section access cleared' : '';
            await this.post_section_access_unassignment(payload, success_message);
        },

        async clear_all_section_access_for_reviewer() {
            this.isLoading = true;

            try {
                await this.clear_all_section_access();
                await this.refresh_section_access_modal_state();
            } catch (error) {
                this.show_error('Error clearing section access');
            }

            this.isLoading = false;
        },

        has_section_access_rows() {
            const has_rows = Array.isArray(this.sections_with_access) && this.sections_with_access.length > 0;
            return has_rows;
        },

        are_all_sections_read_assigned() {
            if (!this.has_section_access_rows()) {
                return false;
            }

            const all_read = this.sections_with_access.every(section => section.has_read === true);
            return all_read;
        },

        are_all_sections_write_assigned() {
            if (!this.has_section_access_rows()) {
                return false;
            }

            const all_write = this.sections_with_access.every(section => section.has_write === true);
            return all_write;
        },

        async toggle_all_read_access() {
            if (this.are_all_sections_read_assigned()) {
                await this.clear_all_section_access_for_reviewer();
                return;
            }

            await this.assign_read_access_to_all();
        },

        async toggle_all_write_access() {
            if (this.are_all_sections_write_assigned()) {
                await this.clear_all_section_access_for_reviewer();
                return;
            }

            await this.assign_write_access_to_all();
        }
    }
};
